// src/utils/cdrUtils.js

/**
 * Tiny helpers
 */
export function str(v) {
    return String(v ?? '').trim()
  }
  
  export function digitsOnly(v) {
    return String(v ?? '').replace(/\D+/g, '')
  }
  
  export function toYmd(d) {
    const dt = new Date(d)
    const y = dt.getFullYear()
    const m = String(dt.getMonth() + 1).padStart(2, '0')
    const day = String(dt.getDate()).padStart(2, '0')
    return `${y}-${m}-${day}`
  }
  
  export function calcRange(datePreset, from, to) {
    const now = new Date()
  
    if (datePreset === 'today') {
      return { date_from: toYmd(now), date_to: toYmd(now) }
    }
    if (datePreset === 'yesterday') {
      const d = new Date(now)
      d.setDate(d.getDate() - 1)
      return { date_from: toYmd(d), date_to: toYmd(d) }
    }
    if (datePreset === '7d') {
      const d = new Date(now)
      d.setDate(d.getDate() - 7)
      return { date_from: toYmd(d), date_to: toYmd(now) }
    }
    if (datePreset === '30d') {
      const d = new Date(now)
      d.setDate(d.getDate() - 30)
      return { date_from: toYmd(d), date_to: toYmd(now) }
    }
    if (datePreset === 'custom') {
      return { date_from: from || null, date_to: to || null }
    }
  
    return { date_from: null, date_to: null }
  }
  
  export function fmtMMSS(sec) {
    sec = Math.max(0, Math.round(sec || 0))
    const m = Math.floor(sec / 60)
    const s = sec % 60
    return `${m}:${String(s).padStart(2, '0')}`
  }
  
  export function fmtHHMM(sec) {
    sec = Math.max(0, Math.round(sec || 0))
    const h = Math.floor(sec / 3600)
    const m = Math.floor((sec % 3600) / 60)
    return `${String(h).padStart(1, '0')}:${String(m).padStart(2, '0')}`
  }
  
  export function fmtWhen(iso) {
    const diff = Date.now() - new Date(iso).getTime()
    const mins = Math.round(diff / 60000)
    if (mins < 1) return 'just now'
    if (mins < 60) return `${mins}m ago`
    const hrs = Math.round(mins / 60)
    if (hrs < 24) return `${hrs}h ago`
    const days = Math.round(hrs / 24)
    return `${days}d ago`
  }
  
  export function dispClass(d) {
    return d === 'ANSWERED'
      ? 'text-emerald-700'
      : d === 'NO ANSWER'
        ? 'text-amber-700'
        : d === 'BUSY' || d === 'FAILED'
          ? 'text-rose-700'
          : 'text-gray-700'
  }
  
  export function dirBadge(d) {
    return d === 'inbound'
      ? 'bg-blue-100 text-blue-800'
      : d === 'outbound'
        ? 'bg-emerald-100 text-emerald-800'
        : 'bg-gray-100 text-gray-800'
  }
  
  /**
   * Parse Asterisk CLID: `"Name" <number>` or `"Name" <TVxxxxx>` etc
   */
  export function parseClid(clid) {
    const s = str(clid)
    // "Kris Drebin" <971...>
    const m = s.match(/^\s*"?([^"]*?)"?\s*<([^>]+)>\s*$/)
    if (m) {
      return {
        name: str(m[1]),
        id: str(m[2])
      }
    }
    // sometimes it's just the number/id
    return { name: '', id: s }
  }
  
  /**
   * Identify Talkvox SIP user ids like TV50654411
   */
  export function isTvSipId(v) {
    const s = str(v).toUpperCase()
    return /^TV\d{6,}$/.test(s)
  }
  
  /**
   * Extract endpoint/user-ish token from a PJSIP channel string:
   * PJSIP/TV60653805-0000006a -> TV60653805
   */
  export function extractEndpointFromChannel(chan) {
    const s = str(chan)
    const m = s.match(/PJSIP\/([^-\s]+)(?:-|$)/i)
    return m?.[1] || ''
  }
  
  /**
   * Pull target out of lastdata:
   * PJSIP/TV50654411/sip:abc@127.0.0.1...
   * PJSIP/+15039139051@telnyx
   * PJSIP/203,,tT
   */
  export function extractTargetFromLastdata(lastdata) {
    const s = str(lastdata)
    const m1 = s.match(/PJSIP\/([^\/@;,\s]+)(?:[\/@;,\s]|$)/i)
    if (m1?.[1]) return m1[1]
    return ''
  }
  
  /**
   * When Asterisk does SendCall hops, dst = SendCall, context = SendCall, etc.
   * We replace the UI dst with something useful.
   */
  export function isSendCallHop(raw) {
    const dst = str(raw?.dst).toLowerCase()
    const ctx = str(raw?.dcontext || raw?.context).toLowerCase()
    return dst === 'sendcall' || ctx === 'sendcall'
  }
  
  /**
   * Normalize "YYYY-MM-DD HH:MM:SS" to ISO
   */
  export function normalizeWhen(v) {
    if (!v) return new Date().toISOString()

    const s = str(v)
    if (s.includes('T') && s.includes('Z')) return new Date(s).toISOString()

    // CDR calldate is stored in UTC — append Z to treat as UTC
    const asUtc = new Date(s.replace(' ', 'T') + 'Z')
    if (!isNaN(asUtc.getTime())) return asUtc.toISOString()

    return new Date().toISOString()
  }
  
  /**
   * Direction inference (exported)
   */
  export function inferDirection(row, trunkHints = ['telnyx', 'bandwidth', 'flowroute', 'twilio', 'voipms']) {
    const ch = str(row.channel).toLowerCase()
    const dch = str(row.dstchannel || row.dstChannel).toLowerCase()
    const src = str(row.src)
    const dst = str(row.dst)
    const context = str(row.dcontext || row.context).toLowerCase()
  
    const hasTrunk = (s) => trunkHints.some(h => s.includes(h))
  
    const isPstnNumber = (n) => {
      const digits = String(n).replace(/[^\d]/g, '')
      return digits.length >= 10
    }
  
    const isExtension = (n) => {
      const digits = String(n).replace(/[^\d]/g, '')
      return digits.length > 0 && digits.length <= 6 && !isPstnNumber(n)
    }
  
    const isLocalChannel = (s) => s.includes('pjsip/') && !hasTrunk(s)
    const isTrunkChannel = (s) => hasTrunk(s) || s.includes('dahdi/') || s.includes('sip/provider')
  
    // 1) strongest: channel legs
    if (isTrunkChannel(ch) && isLocalChannel(dch)) return 'inbound'
    if (isLocalChannel(ch) && isTrunkChannel(dch)) return 'outbound'
  
    // 2) next: destination/src pattern
    if (isExtension(src) && isPstnNumber(dst)) return 'outbound'
    if (isPstnNumber(src) && (isExtension(dst) || !isPstnNumber(dst))) return 'inbound'
  
    // 3) context hint
    if (context.includes('from-trunk') || context.includes('inbound')) return 'inbound'
    if (context.includes('from-internal') || context.includes('outbound')) return 'outbound'
  
    // 4) internal fallback
    if (isExtension(src) && isExtension(dst)) return 'internal'
  
    return 'internal'
  }
  
  // Backwards compat: if anything still imports detectDirection
  export const detectDirection = inferDirection
  
  /**
   * Build a map of SIP user id -> display name from Users payloads.
   * This is intentionally permissive because your Users shapes vary.
   */
  export function buildSipNameMap(users = []) {
    const list = Array.isArray(users) ? users : []
    const map = {}
  
    for (const u of list) {
      const user = u?.user ?? u
      const first = str(user?.first_name || user?.FirstName)
      const last = str(user?.last_name || user?.LastName)
      const full = str(user?.name) || str(`${first} ${last}`).trim()
  
      const sipUsers =
        user?.sip_users ||
        user?.sipUsers ||
        user?.sipUsersList ||
        user?.sip_users_list ||
        user?.sip_users_rel ||
        []
  
      const arr = Array.isArray(sipUsers) ? sipUsers : []
  
      for (const su of arr) {
        const sid = str(su?.sip_user_id || su?.UserID || su?.id || su)
        if (!sid) continue
        map[sid] = full || map[sid] || ''
      }
  
      // sometimes user has a primary sip id
      const primary = str(user?.sip_user_id || user?.sip_user_id_primary || user?.SipUserID)
      if (primary) map[primary] = full || map[primary] || ''
    }
  
    return map
  }
  
  /**
   * Convert a raw "src/dst" value into a friendly label if it's a TVxxxx and we know the name.
   * Falls back to "Soft Phone" when it's TV and no name is available.
   */
  export function labelForSipId(value, sipNameMap = {}) {
    const v = str(value)
    if (!v) return ''
    if (sipNameMap && sipNameMap[v]) return sipNameMap[v]
    if (isTvSipId(v)) return 'Soft Phone'
    return v
  }
  
  /**
   * Recording helpers
   * - Store filename in CDR.userfield as: rec:<filename.wav>
   */
  export function parseRecordingUserfield(userfield) {
    const s = str(userfield)
    const m = s.match(/rec:([^;]+)/i)
    return m?.[1] ? str(m[1]).trim() : ''
  }
  
  export function buildS3RecordingKey(customerId, filename) {
    const cust = str(customerId)
    const file = str(filename)
    if (!cust || !file) return ''
    return `recordings/${cust}/${file}`
  }
  
  /**
   * Map one raw API CDR row -> UI row
   * Optional 2nd param: sipNameMap for "TVxxxx => User Name"
   */
  export function mapCdrRow(raw = {}, sipNameMap = null) {
    const base = {
      id: raw._id ?? raw.id ?? raw.uniqueid,
      uniqueId: raw.uniqueid ?? raw.uniqueId ?? '',
      when: normalizeWhen(raw.calldate ?? raw.when ?? raw.calldate_utc ?? ''),
      clid: raw.clid ?? '',
      src: raw.src ?? '',
      dst: raw.dst ?? '',
      dcontext: raw.dcontext ?? raw.dContext ?? '',
  
      channel: raw.channel ?? '',
      dstchannel: raw.dstchannel ?? raw.dstChannel ?? '',
  
      lastapp: raw.lastapp ?? raw.lastApp ?? '',
      lastdata: raw.lastdata ?? raw.lastData ?? '',
  
      disposition: raw.disposition ?? '',
      accountCode: raw.accountcode ?? raw.accountCode ?? '',
  
      durationSec: Number(raw.duration ?? raw.durationSec ?? 0),
      talkSec: Number(raw.billsec ?? raw.talkSec ?? 0),
  
      // ✅ new: userfield support for recordings
      userfield: raw.userfield ?? raw.userField ?? '',
  
      // existing (future-proof if API returns signed URLs later)
      recordingUrl: raw.recordingUrl ?? raw.recording_url ?? null
    }
  
    // derived recording fields
    const recordingFile = parseRecordingUserfield(base.userfield)
    const hasRecording = !!(base.recordingUrl || recordingFile)
  
    // 1) fix SendCall hop "dst"
    let uiDst = str(base.dst)
    if (isSendCallHop(raw)) {
      const fromLastdata = extractTargetFromLastdata(base.lastdata)
      const fromDstChan = extractEndpointFromChannel(base.dstchannel)
      uiDst = fromLastdata || fromDstChan || uiDst
    }
  
    // 2) direction uses the fixed dst
    const direction = inferDirection({
      channel: base.channel,
      dstchannel: base.dstchannel,
      lastapp: base.lastapp,
      lastdata: base.lastdata,
      src: base.src,
      dst: uiDst,
      dcontext: base.dcontext
    })
  
    // 3) ring estimate
    const ringSec = Math.max(0, base.durationSec - base.talkSec)
  
    // 4) CLID name for outbound "From" label (nice UX)
    const parsed = parseClid(base.clid)
  
    // Use server-enriched labels if available, otherwise fall back to client-side logic
    let srcLabel = raw.from_label || base.src
    if (!raw.from_label) {
      if (direction === 'outbound' && parsed.name) srcLabel = parsed.name
      else srcLabel = labelForSipId(base.src, sipNameMap || {})
    }

    let dstLabel = raw.to_label || labelForSipId(uiDst, sipNameMap || {})
    const toType = raw.to_type || ''
    const via = raw.via || ''
    const path = Array.isArray(raw.path) ? raw.path : []

    return {
      id: String(base.id),
      when: base.when,
  
      uniqueId: base.uniqueId,
      clid: base.clid,
  
      src: base.src,
      srcLabel,
  
      dst: uiDst,
      dstLabel,
      toType,
      via,
      path,

      did: raw.did ?? raw.dnis ?? raw.didNumber ?? '',

      direction: raw.direction || direction,
      disposition: base.disposition,
  
      ringSec,
      talkSec: base.talkSec,
      holdSec: Number(raw.holdsec ?? raw.holdSec ?? 0),
      durationSec: base.durationSec,
  
      route: raw.route ?? '',
      queue: raw.queue ?? '',
      endpoint: raw.endpoint ?? '',
      ivr: raw.ivr ?? '',
  
      // ✅ new recording fields
      userfield: base.userfield,
      hasRecording,
      recordingFile,
  
      // keep existing
      recordingUrl: base.recordingUrl,
  
      fromUser: raw.from_user || null,
      toUser: raw.to_user || null,

      accountCode: base.accountCode,
      channel: base.channel,
      dstChannel: base.dstchannel,
      lastApp: base.lastapp,
      lastData: base.lastdata
    }
  }
  
  /**
   * Optional helper if you want to reduce duplicate rows client-side:
   * For the same uniqueid, prefer ANSWERED; else pick the one with larger billsec/duration.
   */
  export function dedupeByUniqueId(rawRows = []) {
    const list = Array.isArray(rawRows) ? rawRows : []
    const by = new Map()
  
    const score = (r) => {
      const disp = str(r?.disposition).toUpperCase()
      const bill = Number(r?.billsec ?? 0)
      const dur = Number(r?.duration ?? 0)
      const answeredBoost = disp === 'ANSWERED' ? 1000000 : 0
      return answeredBoost + bill * 1000 + dur
    }
  
    for (const r of list) {
      const key = str(r?.uniqueid || r?.uniqueId || r?._id || r?.id)
      if (!key) continue
      const prev = by.get(key)
      if (!prev || score(r) > score(prev)) by.set(key, r)
    }
  
    // keep original-ish ordering by calldate desc if present
    const arr = Array.from(by.values())
    arr.sort((a, b) => {
      const ta = new Date(str(a?.calldate || a?.when || 0).replace(' ', 'T')).getTime()
      const tb = new Date(str(b?.calldate || b?.when || 0).replace(' ', 'T')).getTime()
      return (isNaN(tb) ? 0 : tb) - (isNaN(ta) ? 0 : ta)
    })
  
    return arr
  }
  